import React, { useMemo } from "react";
import { Lock } from "lucide-react";
import PublicPanelIcon from "./icons/PublicPanelIcon";
import { type TokenPair } from "./CombinedTokenCards";
import { formatUsdAmount, sumPublicBalancesUsd, sumPrivateBalancesUsd } from "@/lib/tokenUsdValue";
import { useTokenPrices } from "@/hooks/useTokenPrices";

interface USDBalanceProps {
  /** Token pairs — same data passed to CombinedTokenCards. */
  pairs: TokenPair[];
  /** When false, private portion is hidden from the total (synced with lock state). */
  isPrivateUnlocked?: boolean;
  /** Same handler as ActionBar Lock/Unlock — keeps balance visibility in sync. */
  onToggleLock?: () => void;
}

const USDBalance: React.FC<USDBalanceProps> = ({
  pairs,
  isPrivateUnlocked = false,
}) => {
  const hidePrivate = !isPrivateUnlocked;
  const { prices } = useTokenPrices();

  const publicBalance = useMemo(
    () => sumPublicBalancesUsd(pairs, prices),
    [pairs, prices],
  );

  const privateBalance = useMemo(
    () => sumPrivateBalancesUsd(pairs, prices),
    [pairs, prices],
  );

  const totalBalance = hidePrivate ? publicBalance : publicBalance + privateBalance;

  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden px-3 py-3 sm:px-4 sm:py-5 md:px-8 md:py-7"
      style={{
        background: "transparent",
        borderRadius: "16px",
      }}
    >
      <div className="relative z-10 flex items-center justify-between gap-2">
        {/* Balance */}
        <div className="flex flex-col gap-0.5 sm:gap-1 min-w-0 flex-1">
          <span className="text-muted-foreground text-xs sm:text-sm font-medium">Your balance</span>
          <span className="text-foreground text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
            ${formatUsdAmount(totalBalance)}
          </span>

          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 mt-1.5 sm:mt-3 flex-wrap">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <PublicPanelIcon className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-muted-foreground text-xs font-medium">
                Public ${formatUsdAmount(publicBalance)}
              </span>
            </div>
            {!hidePrivate && (
              <>
                <div className="w-px h-3 sm:h-4 bg-border" />
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="flex items-center justify-center bg-orange-500 rounded-full border-2 border-background w-4 h-4 sm:w-5 sm:h-5 p-[2px] shrink-0">
                    <Lock className="w-full h-full text-foreground" strokeWidth={3} />
                  </div>
                  <span className="text-muted-foreground text-xs font-medium">
                    Private ${formatUsdAmount(privateBalance)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Flags graphic — offset to the side */}
        <img
          src="/coti-cool-logo.svg"
          alt=""
          aria-hidden="true"
          className="pointer-events-none hidden sm:block h-14 md:h-20 w-auto shrink-0 opacity-90 dark:opacity-100"
        />
      </div>
    </div>
  );
};

export default USDBalance;
